"use client"

import { useState, useEffect } from "react"
import { useGoogleAuth } from "@/context/GoogleAuthContext"
import { motion, AnimatePresence } from "framer-motion"
import { FaVideo, FaSpinner, FaTimes, FaExclamationTriangle } from "react-icons/fa"

export default function MeetingFormModal({ isOpen, onClose, onMeetingCreated }) {
  const { createCalendarEvent, isGoogleAuthenticated, signIn } = useGoogleAuth()

  const [formData, setFormData] = useState({
    summary: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    attendees: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [authError, setAuthError] = useState(false)
  const [success, setSuccess] = useState(false)

  // Inicializar fechas con valores por defecto al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      const formatDate = (date) => {
        return date.toISOString().split("T")[0]
      }

      const formatTime = (date) => {
        return date.toTimeString().slice(0, 5)
      }

      // Hora actual redondeada a la siguiente hora
      const startTime = new Date(today)
      startTime.setHours(startTime.getHours() + 1)
      startTime.setMinutes(0)
      startTime.setSeconds(0)

      // Hora de fin (1 hora después)
      const endTime = new Date(startTime)
      endTime.setHours(endTime.getHours() + 1)

      setFormData({
        ...formData,
        startDate: formatDate(today),
        startTime: formatTime(startTime),
        endDate: formatDate(today),
        endTime: formatTime(endTime),
      })
    }
  }, [isOpen])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setAuthError(false)
    setIsSubmitting(true)

    try {
      // Validate form
      if (!formData.summary || !formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) {
        throw new Error("Por favor completa todos los campos obligatorios")
      }

      // Create start and end datetime objects
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`)
      const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`)

      // Validate dates
      if (endDateTime <= startDateTime) {
        throw new Error("La fecha de finalización debe ser posterior a la fecha de inicio")
      }

      // Parse attendees
      const attendeesList = formData.attendees
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email)
        .map((email) => ({ email }))

      // Create event data
      const eventData = {
        summary: formData.summary,
        description: formData.description,
        start: {
          dateTime: startDateTime.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: endDateTime.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        attendees: attendeesList,
        conferenceData: {
          createRequest: {
            requestId: `meet-${Date.now()}`,
            conferenceSolutionKey: { type: "hangoutsMeet" },
          },
        },
      }

      // Create event
      try {
        const result = await createCalendarEvent(eventData)
        console.log("Evento creado:", result)

        // Show success message
        setSuccess(true)

        // Reset form
        setFormData({
          summary: "",
          description: "",
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          attendees: "",
        })

        // Notify parent component
        setTimeout(() => {
          if (onMeetingCreated) onMeetingCreated()
        }, 2000)
      } catch (apiError) {
        console.error("Error API:", apiError)

        // Verificar si es un error de autenticación
        if (
          apiError.status === 401 ||
          (apiError.body && apiError.body.includes("UNAUTHENTICATED")) ||
          (apiError.message && apiError.message.includes("Authentication"))
        ) {
          setAuthError(true)
        } else {
          throw new Error("Error al crear la reunión en Google Calendar. Inténtalo de nuevo.")
        }
      }
    } catch (error) {
      console.error("Error creating event:", error)
      setError(error.message || "Error al crear la reunión")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReauthenticate = () => {
    signIn()
    setAuthError(false)
  }

  const handleClose = () => {
    // Reset form and state when closing
    setFormData({
      summary: "",
      description: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      attendees: "",
    })
    setError("")
    setAuthError(false)
    setSuccess(false)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden"
          >
            <div className="p-6 bg-gradient-to-r from-[var(--navy-blue-light)] to-[var(--navy-blue)] text-white flex justify-between items-center">
              <div className="flex items-center">
                <FaVideo className="h-6 w-6 mr-3" />
                <h1 className="text-2xl font-bold">Programar nueva reunión</h1>
              </div>
              <button onClick={handleClose} className="text-white hover:bg-blue-700 rounded-full p-2 transition-colors">
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-6 text-center"
              >
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">¡Reunión creada con éxito!</h2>
                <p className="text-gray-600 mb-4">Tu reunión ha sido programada correctamente.</p>
                <div className="animate-pulse">
                  <FaSpinner className="animate-spin h-5 w-5 mx-auto text-blue-500" />
                </div>
              </motion.div>
            ) : authError ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-6 text-center"
              >
                <div className="w-16 h-16 bg-yellow-100 text-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExclamationTriangle className="h-8 w-8" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Error de autenticación</h2>
                <p className="text-gray-600 mb-6">
                  Tu sesión ha expirado o no tienes permisos para crear eventos en Google Calendar. Por favor, inicia
                  sesión nuevamente.
                </p>
                <div className="flex justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleReauthenticate}
                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-6 rounded-md font-medium flex items-center justify-center transition-colors"
                  >
                    Iniciar sesión con Google
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="bg-red-50 text-red-500 p-3 rounded-md border border-red-200"
                  >
                    {error}
                  </motion.div>
                )}

                <div>
                  <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">
                    Título de la reunión *
                  </label>
                  <input
                    type="text"
                    id="summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Ej: Reunión de equipo"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Ej: Discutiremos los avances del proyecto"
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de inicio *
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de inicio *
                    </label>
                    <input
                      type="time"
                      id="startTime"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de finalización *
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de finalización *
                    </label>
                    <input
                      type="time"
                      id="endTime"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="attendees" className="block text-sm font-medium text-gray-700 mb-1">
                    Participantes (emails separados por comas)
                  </label>
                  <input
                    type="email"
                    id="attendees"
                    name="attendees"
                    value={formData.attendees}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Ej: usuario1@gmail.com, usuario2@gmail.com"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[var(--gold)] hover:bg-[var(--gold-dark)] text-white py-2 px-6 rounded-md font-medium flex items-center justify-center transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Creando reunión...
                      </>
                    ) : (
                      <>
                        <FaVideo className="mr-2" />
                        Crear reunión de Google Meet
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

